import type { TestSessionAction, TestSessionDiagnostic, TestSessionProblem, TestSessionRequest, TestSessionSnapshot } from './test-session-types'

export type TestSessionTimelineEntry =
  | { type: 'action'; at: number; action: TestSessionAction; requests: TestSessionRequest[] }
  | { type: 'request'; at: number; request: TestSessionRequest }
  | { type: 'diagnostic'; at: number; diagnostic: TestSessionDiagnostic }
  | { type: 'problem'; at: number; problem: TestSessionProblem }

export function getActionDescription(action: Pick<TestSessionAction, 'type' | 'label'>): string {
  const actionName = action.type === 'route' ? '页面' : action.type === 'select' ? '选择' : action.type === 'keyboard' ? '按键' : '点击'
  return `${actionName}：${action.label ?? '未命名操作'}`
}

export function getRequestDescription(request: Pick<TestSessionRequest, 'outcome' | 'status' | 'method' | 'path' | 'durationMs' | 'responseCode' | 'businessOutcome'>): string {
  if (request.businessOutcome === 'failure') {
    return `业务失败${request.responseCode ? ` ${request.responseCode}` : ''} · HTTP ${request.status ?? '未知'} ${request.method} ${request.path}${typeof request.durationMs === 'number' ? ` · ${request.durationMs}ms` : ''}`
  }
  return `${request.outcome === 'success' ? '成功' : '失败'} ${request.status ?? '未知'} ${request.method} ${request.path}${typeof request.durationMs === 'number' ? ` · ${request.durationMs}ms` : ''}`
}

export function matchesTrigger(request: TestSessionRequest, action: TestSessionAction): boolean {
  return request.trigger?.at === action.at
    && request.trigger.route === action.route
    && request.trigger.type === action.type
    && request.trigger.label === action.label
}

export function buildTestSessionTimeline(snapshot: TestSessionSnapshot): TestSessionTimelineEntry[] {
  const actions = snapshot.actions.map((action) => ({
    type: 'action' as const,
    at: action.at,
    action,
    requests: snapshot.requests.filter((request) => matchesTrigger(request, action)),
  }))
  const requests = snapshot.requests
    .filter((request) => !snapshot.actions.some((action) => matchesTrigger(request, action)))
    .map((request) => ({ type: 'request' as const, at: request.at, request }))
  const diagnostics = (snapshot.diagnostics ?? [])
    .filter((diagnostic) => diagnostic.kind === 'runtime')
    .map((diagnostic) => ({ type: 'diagnostic' as const, at: diagnostic.at, diagnostic }))
  const problems = (snapshot.problems ?? []).map((problem) => ({ type: 'problem' as const, at: problem.at, problem }))

  return [...actions, ...requests, ...diagnostics, ...problems].sort((left, right) => left.at - right.at)
}

type ReplayStep = { id: string; at: number; label: string; kind: 'action' | 'request' | 'diagnostic' | 'problem' }

export function getReplaySteps(snapshot: TestSessionSnapshot): ReplayStep[] {
  const steps: ReplayStep[] = []
  buildTestSessionTimeline(snapshot).forEach((entry) => {
    if (entry.type === 'action') {
      steps.push(
        { id: `action-${entry.at}-${entry.action.label ?? ''}`, at: entry.at, label: getActionDescription(entry.action), kind: 'action' as const },
        ...entry.requests.map((request) => ({ id: `request-${request.at}-${request.method}-${request.path}`, at: request.at, label: getRequestDescription(request), kind: 'request' as const })),
      )
      return
    }
    if (entry.type === 'request') {
      steps.push({ id: `request-${entry.at}-${entry.request.method}-${entry.request.path}`, at: entry.at, label: `后台请求：${getRequestDescription(entry.request)}`, kind: 'request' })
      return
    }
    if (entry.type === 'diagnostic') {
      steps.push({ id: `diagnostic-${entry.diagnostic.id}`, at: entry.at, label: `运行时异常：${entry.diagnostic.error.message}`, kind: 'diagnostic' })
      return
    }
    steps.push({ id: entry.problem.id, at: entry.at, label: `问题标记：${entry.problem.actual}`, kind: 'problem' })
  })
  return steps.sort((left, right) => left.at - right.at)
}
